import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  Heading,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Button,
  Spinner,
  UnorderedList,
  OrderedList,
  ListItem,
  Image,
  CloseButton,
} from '@chakra-ui/react';
import { ArrowForwardIcon, AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { uploadReport as uploadReportApi, sendMessageToAgent } from '../../api/chat';
import ChatLogo from '../../assets/images/Chatbot_Logo_Landing_Page.png';

type Msg = { role: 'user' | 'assistant'; content: string };

const SUGGESTIONS: string[] = [
  'Give me an overview of the report.',
  'What are the key genetic findings?',
  "Explain the term 'metabolizer' in plain words.",
  'List all medications mentioned in the report.',
  'Show the Notes & Limitations.',
];

const useSessionId = () => {
  return useMemo(() => {
    const key = 'chat_session_id';
    let sid = localStorage.getItem(key);
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem(key, sid);
    }
    return sid;
  }, []);
};

const cleanAssistantText = (s: string) => {
  let t = s || '';
  t = t.replace(/【[^】]+】/g, '');
  t = t.replace(/^(#{1,6}\s[^\n-]+)\s-\s/gm, '$1\n- ');
  t = t.replace(/\n{3,}/g, '\n\n');
  return t.trim();
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Chatbot: React.FC = () => {
  const sessionId = useSessionId();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'none' | 'ready' | 'error' | 'uploading'>('none');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const cardBg = 'white';
  const userBubbleBg = '#1a1262';
  const botBubbleBg = '#d1d0e0';
  const borderCol = '#d1d0e0';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function uploadReport(file: File) {
    setUploading(true);
    setUploadStatus('uploading');
    setAttachedFile(file);
    try {
      await uploadReportApi(file, sessionId);
      setUploadStatus('ready');
    } catch (e) {
      setUploadStatus('error');
      setAttachedFile(null);
      console.error('Upload error:', e);
    } finally {
      setUploading(false);
    }
  }

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) uploadReport(f);
    e.currentTarget.value = '';
  };

  const removeAttachedFile = () => {
    setAttachedFile(null);
    setUploadStatus('none');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const send = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const userMessage: Msg = { role: 'user', content };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const reply = await sendMessageToAgent(content, { sessionId });
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err: any) {
      const msg = (err?.message || '').toLowerCase().includes('no report uploaded')
        ? 'No report is uploaded yet — please upload the patient report PDF using the paperclip button.'
        : err?.message || 'Something went wrong.';
      setMessages((prev) => [...prev, { role: 'assistant', content: `Error: ${msg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!isOpen && (
        <IconButton
          aria-label="Open PGx Assistant"
          borderRadius="full"
          boxSize="64px"
          p="0"
          bg="linear-gradient(135deg, #1a1262, #3a2db5)"
          border="2px solid"
          borderColor="#d1d0e0"
          boxShadow="0 4px 20px rgba(26, 18, 98, 0.15)"
          position="fixed"
          bottom="100px"
          right="100px"
          zIndex={1400}
          _hover={{ transform: 'scale(1.06)', borderColor: '#1a1262' }}
          _active={{ transform: 'scale(0.98)' }}
          transition="all 0.2s ease"
          onClick={() => setIsOpen(true)}
          icon={
            <Box
              borderRadius="full"
              bg="rgba(255, 255, 255, 0.2)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="100%"
              h="100%"
            >
              <Image src={ChatLogo} alt="Chatbot" w="28px" h="28px" objectFit="contain" />
            </Box>
          }
        />
      )}

      {/* Chat panel */}
      {isOpen && (
        <Box
          position="fixed"
          bottom="60px"
          right="60px"
          w={{ base: '92vw', sm: '420px' }}
          h={{ base: '50vh', sm: '500px' }}
          bg={cardBg}
          border="1px solid"
          borderColor={borderCol}
          borderRadius="20px"
          boxShadow="0 10px 40px rgba(26, 18, 98, 0.15)"
          display="flex"
          flexDirection="column"
          overflow="hidden"
          zIndex={1400}
        >
          {/* Header */}
          <Flex
            justify="space-between"
            align="center"
            px={4}
            py={4}
            bgGradient="linear(135deg, #1a1262 0%, #4f4987 100%)"
            flexShrink={0}
          >
            <Flex align="center" gap={3}>
              <Box>
                <Heading size="sm" color="white" fontWeight="600" m={0}>
                  Seq4life<sup style={{ fontSize: '0.6em', verticalAlign: 'super' }}>TM</sup> AI Assistant
                </Heading>
              </Box>
            </Flex>
            <IconButton
              aria-label="Close"
              icon={<CloseIcon boxSize={3} />}
              size="sm"
              variant="ghost"
              color="rgba(255,255,255,0.8)"
              _hover={{ bg: 'rgba(255,255,255,0.1)', color: 'white' }}
              borderRadius="full"
              onClick={() => setIsOpen(false)}
            />
          </Flex>

          {/* Scrollable messages */}
          <Box flex="1" overflowY="auto" p={4}>
            {messages.length === 0 && (
              <VStack align="flex-start" spacing={4}>
                <Text fontSize="md" color="#8c89b1">
                  Hi there! I'm here to help you with your report. Do you have any questions?
                </Text>
                <VStack align="stretch" spacing={2} w="full">
                  {SUGGESTIONS.map((s) => (
                    <Button
                      key={s}
                      onClick={() => send(s)}
                      variant="ghost"
                      size="sm"
                      w="100%"
                      justifyContent="flex-start"
                      textAlign="left"
                      whiteSpace="normal"
                      px={4}
                      py={3}
                      h="auto"
                      borderRadius="12px"
                      bg="white"
                      border="1px solid"
                      borderColor="#d1d0e0"
                      color="#1a1262"
                      fontWeight="medium"
                      fontSize="sm"
                      _hover={{ bg: '#f8f7fc', borderColor: '#4f4987' }}
                      transition="all 0.2s ease"
                    >
                      {s}
                    </Button>
                  ))}
                </VStack>
              </VStack>
            )}

            {messages.length > 0 && (
              <VStack align="stretch" spacing={3}>
                {messages.map((m, i) => {
                  const isUser = m.role === 'user';
                  return (
                    <Flex key={i} justify={isUser ? 'flex-end' : 'flex-start'}>
                      <Box
                        maxW="85%"
                        px={4}
                        py={3}
                        borderRadius="16px"
                        bg={isUser ? userBubbleBg : botBubbleBg}
                        color={isUser ? 'white' : '#1a1262'}
                        boxShadow="sm"
                        textAlign="left"
                        whiteSpace="pre-wrap"
                        wordBreak="break-word"
                        fontSize="sm"
                      >
                        {isUser ? (
                          m.content
                        ) : (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: (props) => <Heading size="sm" mb={2} textAlign="left" color="#1a1262" {...props} />,
                              h2: (props) => <Heading size="xs" mb={2} textAlign="left" color="#1a1262" {...props} />,
                              h3: (props) => <Heading size="xs" mb={2} textAlign="left" color="#1a1262" {...props} />,
                              p:  (props) => <Text mb={2} textAlign="left" color="#1a1262" {...props} />,
                              ul: (props) => <UnorderedList pl={5} spacing={1} textAlign="left" color="#1a1262" {...props} />,
                              ol: (props) => <OrderedList pl={5} spacing={1} textAlign="left" color="#1a1262" {...props} />,
                              li: (props) => <ListItem mb={1} textAlign="left" {...props} />,
                              strong: (props) => <Text as="span" fontWeight="bold" {...props} />,
                            }}
                          >
                            {cleanAssistantText(m.content)}
                          </ReactMarkdown>
                        )}
                      </Box>
                    </Flex>
                  );
                })}

                {loading && (
                  <Flex justify="flex-start">
                    <Box
                      px={4}
                      py={3}
                      borderRadius="16px"
                      bg={botBubbleBg}
                      boxShadow="sm"
                      display="inline-flex"
                      alignItems="center"
                      gap={2}
                      color="#1a1262"
                      fontSize="sm"
                    >
                      <Spinner size="sm" color="#1a1262" /> Thinking…
                    </Box>
                  </Flex>
                )}
                <div ref={bottomRef} />
              </VStack>
            )}
          </Box>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={onPickFile}
            style={{ display: 'none' }}
          />

          {/* Bottom area: file chip + input */}
          <Box px={4} pb={4} pt={2} borderTop="1px solid" borderColor="#d1d0e0" flexShrink={0}>

            {/* File preview chip */}
            {attachedFile && (
              <Flex
                align="center"
                gap={2}
                mb={2}
                px={3}
                py="6px"
                bg="#f8f7fc"
                border="1px solid"
                borderColor="#d1d0e0"
                borderRadius="10px"
                overflow="hidden"
                position="relative"
                _before={{
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  borderRadius: '10px 0 0 10px',
                  bgGradient: 'linear(to-b, #1a1262, #4f4987)',
                }}
              >
                <Text fontSize="lg" lineHeight="1" flexShrink={0}>📄</Text>

                <Box flex="1" minW={0}>
                  <Text
                    fontSize="xs"
                    fontWeight="600"
                    color="#1a1262"
                    noOfLines={1}
                    title={attachedFile.name}
                  >
                    {attachedFile.name}
                  </Text>
                  <Text fontSize="10px" color="#8c89b1" lineHeight="1.3">
                    {uploading
                      ? 'Uploading…'
                      : uploadStatus === 'ready'
                      ? `${formatBytes(attachedFile.size)} · Ready`
                      : uploadStatus === 'error'
                      ? 'Upload failed'
                      : formatBytes(attachedFile.size)}
                  </Text>
                </Box>

                {uploading ? (
                  <Spinner size="xs" color="#4f4987" flexShrink={0} />
                ) : (
                  <CloseButton
                    size="sm"
                    color="#8c89b1"
                    borderRadius="full"
                    aria-label="Remove file"
                    _hover={{ bg: 'rgba(26,18,98,0.08)', color: '#1a1262' }}
                    onClick={removeAttachedFile}
                    flexShrink={0}
                  />
                )}
              </Flex>
            )}

            {/* Input row */}
            <InputGroup>
              <InputLeftElement width="2.5rem">
                <IconButton
                  aria-label="Upload report PDF"
                  size="sm"
                  variant="ghost"
                  color={attachedFile ? '#4f4987' : '#8c89b1'}
                  _hover={{ color: '#1a1262' }}
                  icon={uploading ? <Spinner size="sm" /> : <AttachmentIcon />}
                  onClick={() => fileInputRef.current?.click()}
                />
              </InputLeftElement>

              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onEnter}
                placeholder="Ask about the uploaded report…"
                borderRadius="full"
                pl="2.75rem"
                pr="4.5rem"
                bg="white"
                border="1px solid"
                borderColor="#d1d0e0"
                color="#1a1262"
                _placeholder={{ color: '#8c89b1', fontSize: 'sm' }}
                _focus={{ bg: 'white', borderColor: '#1a1262' }}
              />

              <InputRightElement width="4.0rem">
                <IconButton
                  aria-label="Send"
                  icon={loading ? <Spinner size="sm" /> : <ArrowForwardIcon />}
                  bg="#1a1262"
                  color="white"
                  _hover={{ bg: '#4f4987' }}
                  _active={{ bg: '#1a1262' }}
                  size="sm"
                  borderRadius="full"
                  onClick={() => send()}
                  isDisabled={loading || !input.trim()}
                />
              </InputRightElement>
            </InputGroup>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Chatbot;
